import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Mail, MapPin, Phone } from "lucide-react";
import { useSettings } from "@/contexts/SettingsContext";
import BrandLogo from "@/components/common/BrandLogo";
import api from "@/lib/api";
import { loadProductCategories } from "@/lib/productCategories";
import { loadIndustries } from "@/lib/industries";
import FooterIndustrialVisual from "@/components/layout/FooterIndustrialVisual";
import FooterSocialLinks from "@/components/layout/FooterSocialLinks";

const MAX_INDUSTRIES = 7;

// Fallback content per column — used until /menus/footer_* loads, or if a menu doc
// comes back empty, so the footer never goes blank. "Resources" has no admin
// "Menus" tab, so it always renders from here.
const STATIC_COLS = {
  Products: [{ label: "All Products", to: "/products" }],
  Industries: [
    { label: "Oil & Gas", to: "/industries" },
    { label: "Power Generation", to: "/industries" },
    { label: "Petrochemical", to: "/industries" },
    { label: "Refineries", to: "/industries" },
    { label: "Cement", to: "/industries" },
    { label: "Steel & Metal", to: "/industries" },
    { label: "Pharmaceutical", to: "/industries" },
  ],
  Resources: [
    { label: "Catalogues", to: "/resources/catalogues" },
    { label: "Datasheets", to: "/resources/datasheets" },
    { label: "Manuals", to: "/resources/operation-manuals" },
    { label: "Videos", to: "/resources/videos" },
    { label: "Case Studies", to: "/resources/case-studies" },
    { label: "FAQs", to: "/resources/faqs" },
    { label: "Certificates", to: "/resources/certificates" },
  ],
  Company: [
    { label: "About Us", to: "/company/about" },
    { label: "Manufacturing Facility", to: "/company/manufacturing-facility" },
    { label: "Quality & Certifications", to: "/company/quality-certifications" },
    { label: "Global Presence", to: "/company/global-presence" },
    { label: "Services", to: "/services" },
    { label: "Contact Us", to: "/contact" },
  ],
};

// Admin → Menus slugs that drive a footer column.
const COLUMN_MENU_SLUG = { Industries: "footer_industries", Company: "footer_company" };

const toLinks = (items) => (items || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((it) => ({ label: it.label, to: it.url || "/" }));

function Column({ title, links, more }) {
  return (
    <nav aria-label={title}>
      <h3 className="mb-5 font-heading text-[12px] font-semibold uppercase tracking-[0.16em] text-white">{title}</h3>
      <ul className="space-y-3">
        {links.map((l) => (
          <li key={`${l.label}-${l.to}`}>
            <Link to={l.to} className="text-[14px] text-white/60 transition-colors duration-200 hover:text-white">{l.label}</Link>
          </li>
        ))}
        {more && (
          <li>
            <Link to={more.to} className="group inline-flex items-center gap-1.5 text-[14px] font-medium text-[#7FB0FF] transition-colors hover:text-white">
              {more.label} <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" aria-hidden="true" />
            </Link>
          </li>
        )}
      </ul>
    </nav>
  );
}

/**
 * Site footer (every public page). A deeper navy than the page sections above it,
 * a 12-column grid aligned to the 1320px page container: brand + description +
 * (optional) social, four link columns, an industrial image strip, then contact. Product links are the real
 * product categories from the Mega Menu; industries/company follow the admin
 * "Menus" when set. Social icons (FooterSocialLinks) render only for URLs saved in Settings.
 */
export default function Footer() {
  const { settings } = useSettings();
  const [menuItems, setMenuItems] = useState({});
  const [categories, setCategories] = useState([]);
  const [industryList, setIndustryList] = useState([]);

  useEffect(() => {
    Object.values(COLUMN_MENU_SLUG).forEach((slug) => {
      api.get(`/menus/${slug}`).then((r) => setMenuItems((prev) => ({ ...prev, [slug]: r.data?.items || [] }))).catch(() => {});
    });
    loadProductCategories().then(setCategories).catch(() => {});
    loadIndustries().then(setIndustryList).catch(() => {});
  }, []);

  const productLinks = categories.length
    ? categories.slice().sort((a, b) => (a.order || 0) - (b.order || 0)).map((c) => ({ label: c.title.trim(), to: `/products/${c.slug}` }))
    : STATIC_COLS.Products;
  const pick = (title) => {
    const items = menuItems[COLUMN_MENU_SLUG[title]];
    return items && items.length ? toLinks(items) : STATIC_COLS[title];
  };
  // The verified Industries collection drives this column; the admin menu / static list is the fallback.
  const industries = industryList.length ? industryList.map((i) => ({ label: i.name, to: `/industries/${i.slug}` })) : pick("Industries");
  const phone = settings?.phone || settings?.mobile;

  return (
    <footer className="border-t border-white/10 bg-[#050F24] text-white" role="contentinfo">
      <div className="mx-auto w-full max-w-[1320px] px-5 py-14 sm:px-8 lg:px-10 lg:py-16">
        <div className="grid grid-cols-2 gap-x-8 gap-y-12 md:grid-cols-4 lg:grid-cols-12">
          <div className="col-span-2 md:col-span-4 lg:col-span-4 lg:pr-8">
            <Link to="/" className="inline-block" aria-label="Shingare Industries — home">
              <BrandLogo variant="white" eager={false} className="h-10 w-auto" />
            </Link>
            <p className="mt-6 max-w-sm text-[14px] leading-relaxed text-white/60">
              Manufacturer and exporter of industrial tube, bolting, pipe preparation and maintenance tools, supplying customers in 45+ countries.
            </p>
            <FooterSocialLinks settings={settings} />
            <div className="mt-6 inline-block rounded-[9px] bg-white p-2">
              <img src="/images/footer/make-in-india.webp" alt="Make in India" width="1200" height="675" loading="lazy" decoding="async" className="h-auto w-[130px] max-w-[140px]" />
            </div>
          </div>

          <div className="lg:col-span-2"><Column title="Products" links={productLinks} more={categories.length ? { label: "All products", to: "/products" } : null} /></div>
          <div className="lg:col-span-2"><Column title="Industries" links={industries.slice(0, MAX_INDUSTRIES)} more={industries.length > MAX_INDUSTRIES ? { label: "All industries", to: "/industries" } : null} /></div>
          <div className="lg:col-span-2"><Column title="Resources" links={STATIC_COLS.Resources} /></div>
          <div className="lg:col-span-2"><Column title="Company" links={pick("Company")} /></div>
        </div>

        {/* Real-application image strip (src/data/footerVisual.js) — separates the link columns from the contact row */}
        <FooterIndustrialVisual />

        {/* Contact strip */}
        <div className="mt-10 grid gap-8 md:grid-cols-2 md:items-start lg:grid-cols-[1fr_1fr_1.4fr_auto]">
          {phone && (
            <a href={`tel:${phone.replace(/\s+/g, "")}`} className="group flex items-start gap-3.5 text-[14px] text-white/70 transition-colors hover:text-white">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/[0.06] text-[#7FB0FF]"><Phone className="h-4 w-4" aria-hidden="true" /></span>
              <span><span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-white/40">Phone</span>{phone}</span>
            </a>
          )}
          {settings?.email && (
            <a href={`mailto:${settings.email}`} className="group flex items-start gap-3.5 break-all text-[14px] text-white/70 transition-colors hover:text-white">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/[0.06] text-[#7FB0FF]"><Mail className="h-4 w-4" aria-hidden="true" /></span>
              <span><span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-white/40">Email</span>{settings.email}</span>
            </a>
          )}
          {settings?.address && (
            <div className="flex items-start gap-3.5 text-[14px] leading-relaxed text-white/70">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/[0.06] text-[#7FB0FF]"><MapPin className="h-4 w-4" aria-hidden="true" /></span>
              <span><span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-white/40">Address</span><span className="whitespace-pre-line">{String(settings.address).replace(/,?\s*\n\s*/g, ", ")}</span></span>
            </div>
          )}
          <a
            href={settings?.google_map_url || "https://maps.google.com"}
            target="_blank"
            rel="noreferrer"
            data-testid="footer-directions"
            className="group inline-flex items-center gap-2 self-start whitespace-nowrap text-[13px] font-semibold uppercase tracking-[0.12em] text-[#7FB0FF] transition-colors hover:text-white"
          >
            Get directions <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" />
          </a>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex w-full max-w-[1320px] flex-col items-center justify-between gap-3 px-5 py-5 text-[13px] text-white/45 sm:px-8 md:flex-row lg:px-10">
          <div>© {new Date().getFullYear()} Shingare Industries Pvt. Ltd. All Rights Reserved.</div>
          <div className="flex gap-6">
            <Link to="#" className="transition-colors hover:text-white">Privacy Policy</Link>
            <Link to="#" className="transition-colors hover:text-white">Terms &amp; Conditions</Link>
            <Link to="/admin/login" data-testid="footer-admin-link" className="transition-colors hover:text-white">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
